import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (index === null) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [index, onClose, onPrev, onNext]);

  useEffect(() => {
    if (index === null) return;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop?.();
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start?.();
    };
  }, [index]);

  const current = images[index];

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-deep/95 backdrop-blur-sm"
          onClick={onClose}
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Fotoğraf büyüteci"
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="lightbox-close"
            aria-label="Kapat"
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full border border-white/25 text-paper transition-colors duration-200 hover:bg-paper hover:text-ink-deep"
          >
            <X size={20} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            data-testid="lightbox-prev"
            aria-label="Önceki fotoğraf"
            className="absolute left-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/25 text-paper transition-colors duration-200 hover:bg-paper hover:text-ink-deep sm:left-6"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            data-testid="lightbox-next"
            aria-label="Sonraki fotoğraf"
            className="absolute right-3 top-1/2 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/25 text-paper transition-colors duration-200 hover:bg-paper hover:text-ink-deep sm:right-6"
          >
            <ChevronRight size={20} />
          </button>

          <motion.img
            key={current.src}
            src={current.src}
            alt={current.alt}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
            className="max-h-[82vh] max-w-[92vw] rounded-md object-contain"
          />

          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full border border-white/20 bg-ink-deep/70 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-paper/80">
            {index + 1} / {images.length} — {current.caption}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}